import { FormError, Label } from '@kcftech/design-system';
import { memo } from 'react';
import {
    Controller,
    useFieldArray,
    useFormContext,
    useFormState,
    useWatch,
} from 'react-hook-form';
import { AlarmThresholdFormFields, INDICATOR_CHART_ITEMS } from './shared';
import {
    PrimaryText,
    SecondaryText,
    StyledBoundsInput,
    StyledCard,
    StyledCheckbox,
    StyledTable,
} from './styled';
import {
    maxAlarmBoundRules,
    minAlarmBoundRules,
    validateAtLeastOneSelectedIndicator,
} from './validation';

type BoundsRowProps = Readonly<{
    index: number;
    item: (typeof INDICATOR_CHART_ITEMS)[number];
}>;

const BoundsRow = memo(function BoundsRow({ index, item }: BoundsRowProps) {
    const { control, getValues, register, trigger } =
        useFormContext<AlarmThresholdFormFields>();
    const handleSelectedChange = (
        checked: boolean,
        onChange: (value: boolean) => void,
    ) => {
        onChange(checked);

        if (isSubmitted) {
            trigger('indicatorTypes');
        }
    };

    const isSelected = useWatch({
        control,
        name: `indicatorTypes.${index}.selected`,
    });

    const { errors, isSubmitted } = useFormState({ control });
    const minAlarmBoundError = errors.indicatorTypes?.[index]?.minAlarmBound?.message;
    const maxAlarmBoundError = errors.indicatorTypes?.[index]?.maxAlarmBound?.message;

    return (
        <tr>
            <td>
                <Controller
                    control={control}
                    name={`indicatorTypes.${index}.selected`}
                    render={({ field }) => (
                        <StyledCheckbox
                            label={
                                <div className="flex-column">
                                    <span>{item.name}</span>
                                    <SecondaryText>{item.units}</SecondaryText>
                                </div>
                            }
                            checked={Boolean(field.value)}
                            onChange={(checked) =>
                                handleSelectedChange(checked, field.onChange)
                            }
                        />
                    )}
                />
            </td>
            <td>
                <div>
                    <StyledBoundsInput
                        $selected={isSelected}
                        aria-invalid={minAlarmBoundError !== undefined}
                        {...register(
                            `indicatorTypes.${index}.minAlarmBound`,
                            minAlarmBoundRules(index, getValues),
                        )}
                    />
                    {minAlarmBoundError && <FormError message={minAlarmBoundError} />}
                </div>
            </td>
            <td>
                <div>
                    <StyledBoundsInput
                        $selected={isSelected}
                        aria-invalid={maxAlarmBoundError !== undefined}
                        {...register(
                            `indicatorTypes.${index}.maxAlarmBound`,
                            maxAlarmBoundRules(index, getValues),
                        )}
                    />
                    {maxAlarmBoundError && <FormError message={maxAlarmBoundError} />}
                </div>
            </td>
        </tr>
    );
});

export function BoundsCard() {
    const { control, trigger } = useFormContext<AlarmThresholdFormFields>();

    const { fields, replace } = useFieldArray({
        control,
        name: 'indicatorTypes',
        rules: {
            validate: validateAtLeastOneSelectedIndicator,
        },
    });

    const { errors, isSubmitted } = useFormState({ control });

    const indicatorTypes = useWatch({
        control,
        name: 'indicatorTypes',
    });

    const allSelected =
        indicatorTypes.length > 0 && indicatorTypes.every((x) => x.selected);

    const handleSelectAll = () => {
        replace(
            indicatorTypes.map((indicator) => ({
                ...indicator,
                selected: !allSelected,
            })),
        );

        if (isSubmitted) {
            trigger('indicatorTypes');
        }
    };

    return (
        <StyledCard>
            <Label label="INDICATORS AND ALARM BOUNDS" />
            {errors.indicatorTypes?.root?.message && (
                <FormError message={errors.indicatorTypes.root.message} />
            )}
            <StyledTable>
                <thead>
                    <tr>
                        <th>
                            <SecondaryText>Indicator Type</SecondaryText>
                        </th>
                        <th colSpan={2}>
                            <SecondaryText>Alarm Bounds</SecondaryText>
                        </th>
                    </tr>
                    <tr>
                        <th>
                            <StyledCheckbox
                                label={<PrimaryText>Select All</PrimaryText>}
                                checked={allSelected}
                                onChange={handleSelectAll}
                            />
                        </th>
                        <th>
                            <Label required label="Min" />
                        </th>
                        <th>
                            <Label required label="Max" />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {fields.map((field, i) => (
                        <BoundsRow
                            key={field.id}
                            index={i}
                            item={INDICATOR_CHART_ITEMS[i]}
                        />
                    ))}
                </tbody>
            </StyledTable>
        </StyledCard>
    );
}
